import { useMemo } from 'react';
import { Download, RefreshCw, RotateCcw } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { FinanceBulkImportRowStatus } from '@/graphql/generatedTypes';
import { ImportTable } from './ImportTable';
import {
  ImportFile,
  ImportResult,
  countByStatus,
  resultsByRowNumber,
} from './importModel';

type ResultPanelProps = {
  file: ImportFile;
  result: ImportResult;
  validating: boolean;
  onValidateAgain: () => void;
  onDownloadResult: () => void;
  onReset: () => void;
};

export function ResultPanel({
  file,
  result,
  validating,
  onValidateAgain,
  onDownloadResult,
  onReset,
}: ResultPanelProps) {
  const results = useMemo(() => resultsByRowNumber(result), [result]);
  const counts = countByStatus(result.rows);
  const applied = counts[FinanceBulkImportRowStatus.Applied];
  const conflicts = counts[FinanceBulkImportRowStatus.Conflict];
  const errors = counts[FinanceBulkImportRowStatus.Error];
  const notApplied = file.rows.filter(
    (row) =>
      results.get(row.rowNumber)?.status !== FinanceBulkImportRowStatus.Applied,
  );
  const failed = conflicts + errors;

  return (
    <div className="tw:flex tw:flex-col tw:gap-4">
      <Alert
        variant={failed > 0 ? 'destructive' : 'default'}
        data-testid="apply-result"
      >
        <AlertTitle>
          {applied} applied · {conflicts}{' '}
          {conflicts === 1 ? 'conflict' : 'conflicts'} · {errors}{' '}
          {errors === 1 ? 'error' : 'errors'}
        </AlertTitle>
        <AlertDescription>
          {failed > 0
            ? 'Rows with a conflict were changed by someone else after the validation and were not written. Validate again to see the current values.'
            : 'Every change was written.'}
        </AlertDescription>
      </Alert>
      <div className="tw:flex tw:flex-wrap tw:gap-2">
        {failed > 0 && (
          <Button disabled={validating} onClick={onValidateAgain}>
            {validating ? <Spinner /> : <RefreshCw />}
            Validate again
          </Button>
        )}
        <Button variant="outline" onClick={onDownloadResult}>
          <Download />
          Download result
        </Button>
        <Button variant="ghost" onClick={onReset}>
          <RotateCcw />
          Start new import
        </Button>
      </div>
      <h3 className="tw:text-base tw:font-semibold">
        Rows not applied ({notApplied.length})
      </h3>
      {notApplied.length > 0 && (
        <ImportTable
          file={file}
          resultFile={file}
          rows={notApplied}
          results={results}
          importedColumns={result.imported_columns}
          countries={[]}
          readOnly
        />
      )}
    </div>
  );
}
